import { useEffect, useState } from 'react';
import {
  fetchTareAmountFeeHistoryLogs,
  fetchTareAmountFeeSetting,
  updateTareAmountFeeSetting,
} from '../services/masterSettingService';

function useTareAmountFeeSetting() {
  const [tareAmountFee, setTareAmountFee] = useState('200');
  const [formError, setFormError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoadError, setHistoryLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage('');
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  useEffect(() => {
    let isMounted = true;

    async function loadTareAmountFee() {
      setIsLoading(true);
      setIsHistoryLoading(true);
      setLoadError('');
      setHistoryLoadError('');

      try {
        const [settingResponse, historyResponse] = await Promise.all([
          fetchTareAmountFeeSetting(),
          fetchTareAmountFeeHistoryLogs(),
        ]);

        if (isMounted) {
          setTareAmountFee(String(settingResponse.tareAmountFee ?? 200));
          setHistoryLogs(historyResponse);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
          setHistoryLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsHistoryLoading(false);
        }
      }
    }

    loadTareAmountFee();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleTareAmountFeeChange(event) {
    setTareAmountFee(event.target.value);
    setFormError('');
    setSubmitError('');
    setSuccessMessage('');
  }

  async function handleTareAmountFeeSubmit(event) {
    event.preventDefault();
    const nextError = validateTareAmountFee(tareAmountFee);

    if (nextError) {
      setFormError(nextError);
      return;
    }

    setIsSaving(true);
    setSubmitError('');
    setSuccessMessage('');

    try {
      const response = await updateTareAmountFeeSetting({
        tareAmountFee: Number(tareAmountFee),
      });
      setTareAmountFee(String(response.tareAmountFee));
      if (response.historyLog) {
        setHistoryLogs((currentLogs) => [response.historyLog, ...currentLogs]);
      }
      setSuccessMessage('Tare amount fee updated successfully.');
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSaving(false);
    }
  }

  return {
    tareAmountFee,
    formError,
    historyLoadError,
    historyLogs,
    isLoading,
    isHistoryLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    handleTareAmountFeeChange,
    handleTareAmountFeeSubmit,
  };
}

function validateTareAmountFee(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return 'Tare amount fee must be zero or greater.';
  }

  return '';
}

export default useTareAmountFeeSetting;
